/**
 * Centralized API Configuration for AIghtBet Frontend
 * Single source of truth for all API and WebSocket endpoints
 * Optimized for Vercel deployment with proper CORS handling
 */

/**
 * Determines if we're running in production environment
 * Updated to handle custom domain routing through Vercel proxy
 */
const isProductionEnvironment = (): boolean => {
  // Always use Vercel proxy for any frontend domain (including custom domains)
  return (
    import.meta.env.PROD ||
    import.meta.env.MODE === 'production' ||
    window.location.hostname.includes('vercel.app') ||
    window.location.hostname.includes('aightbetsports.com') ||
    window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
  );
};

/**
 * Get the API base URL - ALWAYS respects environment variables
 */
const getApiUrl = (): string => {
  // ALWAYS check environment variable first - NO production overrides
  const envApiUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL;

  if (envApiUrl) {
    // Environment variable takes precedence - log for debugging
    console.log('[API Config] Using VITE_API_BASE_URL:', envApiUrl);
    return envApiUrl.replace(/\/$/, '');
  }

  // Development fallback only if no environment variable
  if (!isProductionEnvironment()) {
    console.log('[API Config] Development fallback: localhost:10000');
    return 'http://localhost:10000';
  }

  // Production uses api.aightbetsports.com via Cloudflare
  console.log('[API Config] Using production API at api.aightbetsports.com');
  return 'https://api.aightbetsports.com';
};

/**
 * Get the WebSocket URL - ALWAYS respects environment variables
 */
const getWebSocketUrl = (): string => {
  // ALWAYS check environment variable first - NO production overrides
  const envWsUrl = import.meta.env.VITE_WS_URL || import.meta.env.VITE_WEBSOCKET_URL;

  if (envWsUrl) {
    // Environment variable takes precedence - log for debugging
    console.log('[WebSocket Config] Using VITE_WS_URL:', envWsUrl);
    return envWsUrl;
  }

  // Auto-derive from API URL if set
  const apiUrl = import.meta.env.VITE_API_BASE_URL;
  if (apiUrl) {
    const wsUrl = apiUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    console.log('[WebSocket Config] Auto-derived from API URL:', wsUrl);
    return wsUrl;
  }

  // Development fallback only if no environment variables
  if (!isProductionEnvironment()) {
    console.log('[WebSocket Config] Development fallback: ws://localhost:10000');
    return 'ws://localhost:10000';
  }

  // Production uses api.aightbetsports.com via Cloudflare
  console.log('[WebSocket Config] Using production WSS at api.aightbetsports.com');
  return 'wss://api.aightbetsports.com';
};

/**
 * Get Socket.IO URL - ALWAYS respects environment variables
 */
const getSocketIOUrl = (): string => {
  // ALWAYS check environment variable first - NO production overrides
  const envWsUrl = import.meta.env.VITE_WS_URL || import.meta.env.VITE_WEBSOCKET_URL;

  if (envWsUrl) {
    // Convert WebSocket URL to HTTP URL for Socket.IO
    // Remove any /ws path suffix since Socket.IO uses /socket.io by default
    let socketIOUrl = envWsUrl.replace(/^wss?/, 'https').replace(/^ws/, 'http');

    // Remove /ws or /ws/ from the end of the URL if present
    socketIOUrl = socketIOUrl.replace(/\/ws\/?$/, '');

    console.log('[Socket.IO Config] Using derived from WS URL:', socketIOUrl);
    return socketIOUrl;
  }

  // Auto-derive from API URL if set
  const apiUrl = import.meta.env.VITE_API_BASE_URL;
  if (apiUrl) {
    // Ensure no trailing slash
    const cleanApiUrl = apiUrl.replace(/\/$/, '');
    console.log('[Socket.IO Config] Auto-derived from API URL:', cleanApiUrl);
    return cleanApiUrl;
  }

  // Development fallback only if no environment variables
  if (!isProductionEnvironment()) {
    console.log('[Socket.IO Config] Development fallback: http://localhost:10000');
    return 'http://localhost:10000';
  }

  // Production uses api.aightbetsports.com via Cloudflare
  console.log('[Socket.IO Config] Using production Socket.IO at api.aightbetsports.com');
  return 'https://api.aightbetsports.com';
};

// Export centralized configuration
export const API_URL = getApiUrl();
export const WS_URL = getWebSocketUrl();

// Export utility functions for other modules to use
export { isProductionEnvironment, getApiUrl, getWebSocketUrl, getSocketIOUrl };

// Configuration constants
export const CONFIG = {
  API_TIMEOUT: 300000, // 5 minutes for AI analysis (Claude extended thinking)
  QUICK_TIMEOUT: 15000, // 15 seconds for quick reads (games, auth, etc.)
  STANDARD_TIMEOUT: 60000, // 60 seconds for standard operations
  WS_RECONNECT_ATTEMPTS: 5,
  WS_RECONNECT_DELAY: 1000,
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
} as const;

// API endpoints
export const API_ENDPOINTS = {
  // Games
  games: `${API_URL}/api/games`,
  liveGames: `${API_URL}/api/games/live`,
  gameDetails: (id: string) => `${API_URL}/api/games/${id}`,
  
  // Predictions
  predictions: `${API_URL}/api/predictions`,
  aiAnalysis: `${API_URL}/api/ai/analysis`,
  
  // Betting
  odds: `${API_URL}/api/odds`,
  lines: `${API_URL}/api/lines`,
  bets: `${API_URL}/api/bets`,
  
  // Health
  health: `${API_URL}/api/health`,
};

// WebSocket events
export const WS_EVENTS = {
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  GAME_UPDATE: 'game_update',
  ODDS_UPDATE: 'odds_update',
  PREDICTION_UPDATE: 'prediction_update',
  ERROR: 'error',
};

export default {
  API_URL,
  WS_URL,
  API_ENDPOINTS,
  WS_EVENTS,
};